import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import paymentService from '../../services/paymentService';
import contractorMarketplaceService from '../../services/contractorMarketplaceService';
import api from '../../services/api';
import ContractorSmartAssistant from '../dashboard/ContractorSmartAssistant';
import EnhancedContractorStatCards from '../dashboard/EnhancedContractorStatCards';
import useProfileGate from '../../hooks/useProfileGate';
import WithdrawalDialog from '../common/WithdrawalDialog';
import PendingVerificationBanner from '../PendingVerificationBanner';
import LocationGateBanner from '../common/LocationGateBanner';
import { brand, status, text, bg, border } from '../../theme/colors';

/**
 * Enhanced Contractor Dashboard - Drop-in replacement for ContractorDashboard.js
 * Maintains all existing functionality while adding:
 * - Smart Insights
 * - Enhanced stat cards with gradients
 * - Better visual design matching Client Dashboard
 */
const ContractorDashboard = () => {
    const { user, getApiClient, updateUser } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { profileData, hasVerifiablePortfolio, isContractor } = useProfileGate();
    const approvalStatus = user?.approvalStatus || user?.profile?.approvalStatus || 'approved';
    const isApproved = approvalStatus === 'approved';
    const [dashboardData, setDashboardData] = useState({
        activeProjects: [],
        availableJobs: [],
        milestones: [],
        earnings: [],
        stats: {
            activeProjects: 0,
            completedProjects: 0,
            totalEarnings: 0,
            pendingPayments: 0,
            rating: 0,
            reviewCount: 0
        }
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [completionPercentage, setCompletionPercentage] = useState(null);
    const [availableBalance, setAvailableBalance] = useState(null);
    const [withdrawalDialogOpen, setWithdrawalDialogOpen] = useState(false);
    const [hasBrowsedProjects, setHasBrowsedProjects] = useState(false);
    const [hasSubmittedProposal, setHasSubmittedProposal] = useState(false);
    const [pendingInviteQuotes, setPendingInviteQuotes] = useState([]);
    const [bidQueueSummary, setBidQueueSummary] = useState({
        drafts: 0,
        revisions: 0,
        submitted: 0,
        accepted: 0
    });

    // Mark welcome as seen on first dashboard visit (enables Smart Insights for next login)
    useEffect(() => {
        if (user?.id && !user?.profile?.welcomeSeen) {
            api.put('/auth/users/profile', { welcome_seen: true })
                .then(() => {
                    updateUser({
                        profile: {
                            ...user?.profile,
                            welcomeSeen: true
                        }
                    });
                })
                .catch(() => {});
        }
    }, [user?.id, user?.profile?.welcomeSeen, updateUser]);

    const loadDashboardData = useCallback(async () => {
        if (!user?.id) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);

            // Fetch only this contractor's assigned projects (backend filters on contractorId)
            let myProjects = [];
            try {
                const projectsRes = await api.get('/projects', { params: { contractorId: user.id, limit: 100 } });
                myProjects = Array.isArray(projectsRes.data?.data?.projects)
                    ? projectsRes.data.data.projects
                    : [];
            } catch (_) {
                // Non-critical — stats fall back to zero
            }

            const activeProjects = myProjects.filter(
                p => String(p.contractor_id) === String(user.id) && !['completed', 'cancelled'].includes(p.status)
            );

            // Try to fetch payments, but don't fail if endpoint doesn't exist
            let earnings = [];
            let totalEarnings = 0;
            let pendingPayments = 0;

            try {
                const paymentsResponse = await paymentService.getPayments();
                earnings = Array.isArray(paymentsResponse?.payments)
                    ? paymentsResponse.payments
                        .filter(payment => payment.status === 'completed')
                        .map(payment => ({
                            month: new Date(payment.created_at).toLocaleString('default', { month: 'short' }),
                            // pg numeric comes back as a string — coerce before summing
                            amount: parseFloat(payment.amount) || 0,
                            project: payment.project_title || payment.project_id
                        }))
                    : [];

                totalEarnings = earnings.reduce((sum, item) => sum + (item.amount || 0), 0);
                pendingPayments = Array.isArray(paymentsResponse?.payments)
                    ? paymentsResponse.payments
                        .filter(payment => ['pending', 'processing'].includes(payment.status))
                        .reduce((sum, payment) => sum + (parseFloat(payment.amount) || 0), 0)
                    : 0;
            } catch (paymentError) {
                console.log('Payments endpoint not available, using defaults');
                // Continue with empty payment data
            }

            // Fetch available balance for wallet widget
            try {
                const balanceRes = await api.get('/payments/balance');
                if (balanceRes.data?.data?.available !== undefined) {
                    setAvailableBalance(balanceRes.data.data.available);
                }
            } catch (_) {
                // Non-critical — widget will show loading state
            }

            // Completion percentage comes from the useProfileGate context, not the
            // profile fetch below, so set it independently of that request.
            setCompletionPercentage(profileData?.completionPercentage ?? 0);

            // Fetch contractor profile to capture the real computed stats the
            // endpoint already returns (rating, review count, completed projects).
            let profileRating = 0;
            let profileReviewCount = 0;
            let profileCompletedProjects = 0;
            try {
                const profileRes = await getApiClient().get('/contractors/profile/me');
                if (profileRes.data.success) {
                    const p = profileRes.data.data;
                    profileRating = parseFloat(p.rating || 0);
                    profileReviewCount = parseInt(p.reviewCount || 0, 10);
                    profileCompletedProjects = parseInt(p.completedProjects || 0, 10);
                }
            } catch (_) {
                // Non-critical — Smart Insights will show default state
            }

            setHasBrowsedProjects(
                !!user?.profile?.browsedProjects ||
                user?.profile?.browsed_projects === true ||
                localStorage.getItem('eqaya_marketplace_intro_seen') === 'true'
            );

            try {
                const [proposalResult, assessmentResult, bidResult] = await Promise.all([
                    contractorMarketplaceService.getMyProposals(),
                    contractorMarketplaceService.getMyAssessmentRequests(),
                    api.get('/bids/contractor/me').catch(() => null)
                ]);
                const proposals = proposalResult.success ? proposalResult.data?.proposals || [] : [];
                const assessmentRequests = assessmentResult.success ? assessmentResult.data?.assessmentRequests || [] : [];
                const bids = Array.isArray(bidResult?.data?.data) ? bidResult.data.data : [];
                setBidQueueSummary({
                    drafts: bids.filter(bid => bid.status === 'draft').length,
                    revisions: bids.filter(bid => bid.status === 'changes_requested').length,
                    submitted: bids.filter(bid => ['submitted', 'under_review'].includes(bid.status)).length,
                    accepted: bids.filter(bid => bid.status === 'accepted').length
                });
                const hasSubmitted = proposals.length > 0 || assessmentRequests.length > 0 || bids.length > 0;
                setHasSubmittedProposal(hasSubmitted || localStorage.getItem('eqaya_first_proposal_submitted') === 'true');
                if (hasSubmitted) {
                    localStorage.setItem('eqaya_first_proposal_submitted', 'true');
                }
            } catch (_) {
                setHasSubmittedProposal(localStorage.getItem('eqaya_first_proposal_submitted') === 'true');
            }

            setDashboardData({
                activeProjects,
                availableJobs: [],
                milestones: [],
                earnings,
                stats: {
                    activeProjects: activeProjects.length,
                    completedProjects: profileCompletedProjects || myProjects.filter(p => String(p.contractor_id) === String(user.id) && p.status === 'completed').length,
                    totalEarnings,
                    pendingPayments,
                    rating: profileRating,
                    reviewCount: profileReviewCount
                }
            });

            // Fetch pending quote requests (including invite-generated ones)
            try {
                const quotesResponse = await api.get('/quote-requests');
                // API returns { data: { quoteRequests, count } }; tolerate either shape.
                const quoteList = Array.isArray(quotesResponse.data?.data)
                    ? quotesResponse.data.data
                    : (quotesResponse.data?.data?.quoteRequests || []);
                const pending = quoteList.filter(q => q.status === 'pending' && q.contractor_id === user.id);
                setPendingInviteQuotes(pending);
            } catch (quoteErr) {
                // Non-critical — don't fail dashboard load
                console.error('Failed to load quote requests:', quoteErr);
            }

        } catch (err) {
            setError(err.message || 'Failed to load contractor dashboard');
            console.error('Failed to load contractor dashboard:', err);
        } finally {
            setLoading(false);
        }
    }, [profileData?.completionPercentage, user?.id]);

    useEffect(() => {
        loadDashboardData();
    }, [loadDashboardData]);

    const getStatusColor = (statusKey) => {
        const colors = {
            'in_progress': status.warningDark,
            'planning': status.infoDark,
            'completed': status.successDark,
            'pending': text.tertiary,
            'on_hold': status.errorDark
        };
        return colors[statusKey] || text.tertiary;
    };

    const formatCurrency = (amount, currency = 'USD') => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency
        }).format(parseFloat(amount) || 0);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString();
    };

    const handleApplyForJob = (jobId) => {
        navigate('/contractor/marketplace', { state: { focusJobId: jobId } });
    };

    const handleBrowseJobs = () => {
        navigate('/contractor/marketplace');
    };

    const handleSubmitMilestone = () => {
        navigate('/projects');
    };

    const handleViewProfile = () => {
        navigate('/profile');
    };

    const handlePaymentHistory = () => {
        navigate('/contractor/finances');
    };

    const getNextStep = () => {
        if (!hasVerifiablePortfolio) {
            return {
                title: 'Add one verifiable portfolio project',
                body: 'You can browse and draft quotations now. Add a completed project with real details and a finished photo before submitting to clients.',
                cta: 'Add Portfolio',
                onClick: () => navigate('/contractor/portfolio')
            };
        }

        if (bidQueueSummary.revisions > 0) {
            return {
                title: 'Revise a quotation requested by a client',
                body: `${bidQueueSummary.revisions} quotation${bidQueueSummary.revisions === 1 ? ' needs' : 's need'} your changes before it can move forward.`,
                cta: 'Open Quotation Queue',
                onClick: () => navigate('/bids')
            };
        }

        if (bidQueueSummary.drafts > 0) {
            return {
                title: 'Finish and submit your draft quotation',
                body: `${bidQueueSummary.drafts} draft quotation${bidQueueSummary.drafts === 1 ? ' is' : 's are'} waiting. Review the document, then submit it when ready.`,
                cta: 'Continue Draft',
                onClick: () => navigate('/bids')
            };
        }

        if (!hasBrowsedProjects) {
            return {
                title: 'Find your first project opportunity',
                body: 'Start from Find Work, open a project, then either request missing details or build a professional bid.',
                cta: 'Find Work',
                onClick: handleBrowseJobs
            };
        }

        if (bidQueueSummary.submitted > 0) {
            return {
                title: 'Track bids waiting for client response',
                body: `${bidQueueSummary.submitted} submitted bid${bidQueueSummary.submitted === 1 ? ' is' : 's are'} waiting for review.`,
                cta: 'View Status',
                onClick: () => navigate('/bids')
            };
        }

        return {
            title: 'Browse open projects',
            body: 'Open a project, request details if anything is missing, then build a professional bid only when you can price it responsibly.',
            cta: 'Find Work',
            onClick: handleBrowseJobs
        };
    };

    const handleSubmitMilestoneForProject = (projectId, milestoneId) => {
        navigate('/projects', { state: { focusProjectId: projectId, focusMilestoneId: milestoneId } });
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                <div style={{ fontSize: '18px', color: text.secondary }}>Loading your dashboard...</div>
            </div>
        );
    }

    return (
        <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
            {/* Header */}
            <div style={{ marginBottom: '30px' }}>
                <h1 style={{ color: text.primary, marginBottom: '10px', fontSize: '32px', fontWeight: '700' }}>
                    Dashboard
                </h1>
                <p style={{ color: text.secondary, fontSize: '16px' }}>
                    Manage your construction projects and grow your business
                </p>
            </div>

            {/* Success message from onboarding */}
            {location.state?.message && (
                <div style={{
                    backgroundColor: location.state.type === 'success' ? '#f0fdf4' : location.state.type === 'warning' ? '#fffbeb' : '#eff6ff',
                    color: location.state.type === 'success' ? '#166534' : location.state.type === 'warning' ? '#92400e' : '#1e40af',
                    padding: '15px 20px',
                    borderRadius: '10px',
                    marginBottom: '20px',
                    border: `1px solid ${location.state.type === 'success' ? '#bbf7d0' : location.state.type === 'warning' ? '#fde68a' : '#bfdbfe'}`,
                    fontSize: '15px'
                }}>
                    {location.state.message}
                </div>
            )}

            {error && (
                <div style={{
                    backgroundColor: '#fdf2f2',
                    color: status.errorDark,
                    padding: '15px',
                    borderRadius: '8px',
                    marginBottom: '20px',
                    border: '1px solid #fecaca'
                }}>
                    {error}
                </div>
            )}

            {/* Pending Verification Banner — shown when account is not yet approved */}
            <PendingVerificationBanner approvalStatus={approvalStatus} />

            {/* Location gate — shown when Province/District are missing (blocks bidding) */}
            <LocationGateBanner />

            {/* Pending Quote Requests Banner — shown when contractor has invite-generated quote requests */}
            {pendingInviteQuotes.length > 0 && (
                <div style={{
                    background: brand.gradient,
                    color: 'white',
                    padding: '20px 24px',
                    borderRadius: '14px',
                    marginBottom: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: '12px'
                }}>
                    <div style={{ flex: '1 1 300px' }}>
                        <div style={{ fontSize: '18px', fontWeight: '700', marginBottom: '4px' }}>
                            {pendingInviteQuotes.length === 1
                                ? 'You have a project quote request waiting!'
                                : `You have ${pendingInviteQuotes.length} project quote requests waiting!`}
                        </div>
                        <div style={{ fontSize: '14px', opacity: 0.9 }}>
                            {pendingInviteQuotes[0]?.project_description
                                ? `"${pendingInviteQuotes[0].project_description.substring(0, 100)}${pendingInviteQuotes[0].project_description.length > 100 ? '...' : ''}"`
                                : 'Review the project details and submit your quote.'}
                        </div>
                    </div>
                    {isApproved && (
                        <button
                            onClick={() => navigate('/received-quotes')}
                            style={{
                                padding: '12px 24px',
                                backgroundColor: 'white',
                                color: brand.primary,
                                border: 'none',
                                borderRadius: '10px',
                                cursor: 'pointer',
                                fontWeight: '600',
                                fontSize: '15px'
                            }}
                        >
                            Review & Quote
                        </button>
                    )}
                </div>
            )}

            {/* One clear contractor next step */}
            {isContractor && (() => {
                const nextStep = getNextStep();
                return (
                    <div style={{
                        backgroundColor: 'white',
                        color: text.primary,
                        padding: '22px 26px',
                        borderRadius: '14px',
                        marginBottom: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: '16px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                        border: `1px solid ${border.light}`
                    }}>
                        <div style={{ flex: '1 1 300px' }}>
                            <div style={{ fontSize: '13px', color: brand.primary, fontWeight: '700', textTransform: 'uppercase', marginBottom: '6px' }}>
                                Next Step
                            </div>
                            <div style={{ fontSize: '20px', fontWeight: '700', marginBottom: '6px' }}>
                                {nextStep.title}
                            </div>
                            <div style={{ fontSize: '14px', color: text.secondary }}>
                                {nextStep.body}
                            </div>
                        </div>
                        <button
                            onClick={nextStep.onClick}
                            style={{
                                padding: '13px 24px',
                                background: brand.gradient,
                                color: 'white',
                                border: 'none',
                                borderRadius: '10px',
                                cursor: 'pointer',
                                fontSize: '15px',
                                fontWeight: '700',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {nextStep.cta}
                        </button>
                    </div>
                );
            })()}

            {/* Smart Insights — hidden on first login */}
            {user?.profile?.welcomeSeen && (
                <div style={{ marginBottom: '30px' }}>
                    <ContractorSmartAssistant
                        contractorProfile={{
                            specialty: user?.profile?.specialty,
                            location: user?.profile?.location || user?.profile?.city,
                            bio: user?.profile?.bio,
                            city: user?.profile?.city,
                            portfolio_complete: user?.profile?.portfolioComplete
                        }}
                        completionPercentage={completionPercentage}
                    />
                </div>
            )}

            {/* Enhanced Stats Cards */}
            <div style={{ marginBottom: '30px' }}>
                <EnhancedContractorStatCards 
                    stats={dashboardData.stats}
                    trends={dashboardData.trends}
                />
            </div>

            {/* Wallet Balance Banner */}
            <div style={{
                background: brand.gradient,
                borderRadius: '14px',
                padding: '24px 28px',
                marginBottom: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '16px',
                boxShadow: '0 4px 15px rgba(102,126,234,0.35)'
            }}>
                <div>
                    <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px', fontWeight: '600', letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: '6px' }}>
                        Available to Withdraw
                    </div>
                    <div style={{ color: 'white', fontSize: '32px', fontWeight: '800' }}>
                        {availableBalance === null
                            ? '...'
                            : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(availableBalance)
                        }
                    </div>
                    <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px', marginTop: '4px' }}>
                        Earned from completed milestones
                    </div>
                </div>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <button
                        onClick={() => setWithdrawalDialogOpen(true)}
                        disabled={!availableBalance || availableBalance <= 0}
                        style={{
                            padding: '12px 24px',
                            backgroundColor: 'white',
                            color: brand.primary,
                            border: 'none',
                            borderRadius: '10px',
                            cursor: availableBalance > 0 ? 'pointer' : 'not-allowed',
                            fontSize: '15px',
                            fontWeight: '700',
                            opacity: availableBalance > 0 ? 1 : 0.6,
                            transition: 'background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1), color 0.2s cubic-bezier(0.4, 0, 0.2, 1), transform 0.15s cubic-bezier(0.34, 1.56, 0.64, 1)'
                        }}
                    >
                        Withdraw Funds
                    </button>
                    <button
                        onClick={() => navigate('/contractor/finances')}
                        style={{
                            padding: '12px 24px',
                            backgroundColor: 'rgba(255,255,255,0.15)',
                            color: 'white',
                            border: '2px solid rgba(255,255,255,0.5)',
                            borderRadius: '10px',
                            cursor: 'pointer',
                            fontSize: '15px',
                            fontWeight: '600',
                            transition: 'background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1), color 0.2s cubic-bezier(0.4, 0, 0.2, 1), transform 0.15s cubic-bezier(0.34, 1.56, 0.64, 1)'
                        }}
                    >
                        View Earnings
                    </button>
                </div>
            </div>

            {/* Quick Actions */}
            <div style={{
                backgroundColor: 'white',
                padding: '25px',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                marginBottom: '30px'
            }}>
                <h2 style={{ color: text.primary, marginTop: 0, marginBottom: '20px', fontSize: '20px', fontWeight: '600' }}>
                    Quick Actions
                </h2>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                    {isApproved && (
                        <button
                            onClick={handleBrowseJobs}
                            style={{
                                padding: '16px',
                                backgroundColor: 'white',
                                color: text.primary,
                                border: `2px solid ${border.light}`,
                                borderRadius: '10px',
                                cursor: 'pointer',
                                fontSize: '16px',
                                fontWeight: '600',
                                transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                            }}
                            onMouseOver={(e) => {
                                e.target.style.transform = 'translateY(-2px)';
                                e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                                e.target.style.borderColor = text.primary;
                            }}
                            onMouseOut={(e) => {
                                e.target.style.transform = 'translateY(0)';
                                e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                                e.target.style.borderColor = border.light;
                            }}
                        >
                            Find Work
                        </button>
                    )}
                    {isApproved && (
                        <button
                            onClick={() => navigate('/bids')}
                            style={{
                                padding: '16px',
                                backgroundColor: 'white',
                                color: text.primary,
                                border: `2px solid ${border.light}`,
                                borderRadius: '10px',
                                cursor: 'pointer',
                                fontSize: '16px',
                                fontWeight: '600',
                                transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                            }}
                            onMouseOver={(e) => {
                                e.target.style.transform = 'translateY(-2px)';
                                e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                                e.target.style.borderColor = text.primary;
                            }}
                            onMouseOut={(e) => {
                                e.target.style.transform = 'translateY(0)';
                                e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                                e.target.style.borderColor = border.light;
                            }}
                        >
                            Quotes
                        </button>
                    )}
                    <button
                        onClick={handleViewProfile}
                        style={{
                            padding: '16px',
                            backgroundColor: 'white',
                            color: text.primary,
                            border: `2px solid ${border.light}`,
                            borderRadius: '10px',
                            cursor: 'pointer',
                            fontSize: '16px',
                            fontWeight: '600',
                            transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                        }}
                        onMouseOver={(e) => {
                            e.target.style.transform = 'translateY(-2px)';
                            e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                            e.target.style.borderColor = text.primary;
                        }}
                        onMouseOut={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                            e.target.style.borderColor = border.light;
                        }}
                    >
                        Profile
                    </button>
                    <button
                        onClick={() => navigate('/contractor/portfolio')}
                        style={{
                            padding: '16px',
                            backgroundColor: 'white',
                            color: text.primary,
                            border: `2px solid ${border.light}`,
                            borderRadius: '10px',
                            cursor: 'pointer',
                            fontSize: '16px',
                            fontWeight: '600',
                            transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                        }}
                        onMouseOver={(e) => {
                            e.target.style.transform = 'translateY(-2px)';
                            e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                            e.target.style.borderColor = text.primary;
                        }}
                        onMouseOut={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                            e.target.style.borderColor = border.light;
                        }}
                    >
                        Portfolio
                    </button>
                    <button
                        onClick={() => navigate('/messages')}
                        style={{
                            padding: '16px',
                            backgroundColor: 'white',
                            color: text.primary,
                            border: `2px solid ${border.light}`,
                            borderRadius: '10px',
                            cursor: 'pointer',
                            fontSize: '16px',
                            fontWeight: '600',
                            transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                        }}
                        onMouseOver={(e) => {
                            e.target.style.transform = 'translateY(-2px)';
                            e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                            e.target.style.borderColor = text.primary;
                        }}
                        onMouseOut={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                            e.target.style.borderColor = border.light;
                        }}
                    >
                        Messages
                    </button>
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '30px' }}>
                {/* Active Projects */}
                <div style={{ 
                    backgroundColor: 'white', 
                    padding: '25px', 
                    borderRadius: '12px', 
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)' 
                }}>
                    <h2 style={{ color: text.primary, marginBottom: '20px', fontSize: '20px', fontWeight: '600' }}>
                        Active Projects
                    </h2>

                    {dashboardData.activeProjects.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '60px 20px', color: text.secondary }}>
                            <div style={{ fontSize: '48px', marginBottom: '15px' }}>🏗️</div>
                            <h3 style={{ marginBottom: '10px' }}>No Active Projects</h3>
                            <p>Find work, submit a bid, and your won projects will appear here.</p>
                            {isApproved && (
                                <button
                                    onClick={handleBrowseJobs}
                                    style={{
                                        marginTop: '16px',
                                        padding: '12px 24px',
                                        backgroundColor: brand.primary,
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        fontWeight: '700'
                                    }}
                                >
                                    Find Work
                                </button>
                            )}
                        </div>
                    ) : (
                        <div>
                            {dashboardData.activeProjects.map(project => (
                                <div key={project.id} style={{
                                    border: `1px solid ${border.light}`,
                                    borderRadius: '10px',
                                    padding: '20px',
                                    marginBottom: '20px',
                                    transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    cursor: 'pointer'
                                }}
                                onMouseOver={(e) => {
                                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                }}
                                onMouseOut={(e) => {
                                    e.currentTarget.style.boxShadow = 'none';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                }}
                                onClick={() => navigate(`/projects/${project.id}`)}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '15px' }}>
                                        <div>
                                            <h3 style={{ margin: '0 0 5px 0', color: text.primary, fontSize: '18px' }}>{project.title || project.name}</h3>
                                            <p style={{ margin: '0 0 5px 0', color: text.secondary, fontSize: '14px' }}>
                                                Client: {project.client?.name || project.client || 'Project owner'} • {project.location}
                                            </p>
                                            <p style={{ margin: 0, color: text.secondary, fontSize: '14px' }}>
                                                Deadline: {project.deadline ? formatDate(project.deadline) : 'Not set'}
                                            </p>
                                        </div>
                                        <span style={{
                                            padding: '6px 14px',
                                            borderRadius: '20px',
                                            backgroundColor: getStatusColor(project.status),
                                            color: 'white',
                                            fontSize: '12px',
                                            fontWeight: 'bold'
                                        }}>
                                            {project.status.replace('_', ' ').toUpperCase()}
                                        </span>
                                    </div>

                                    <div style={{ marginBottom: '15px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', marginBottom: '8px', fontWeight: '600' }}>
                                            <span>Progress: {Math.round(parseFloat(project.completion_percentage) || 0)}%</span>
                                            {parseFloat(project.budget) > 0 && (
                                                <span>Budget: {formatCurrency(project.budget)}</span>
                                            )}
                                        </div>
                                        <div style={{ 
                                            width: '100%', 
                                            height: '10px', 
                                            backgroundColor: '#ecf0f1', 
                                            borderRadius: '5px',
                                            overflow: 'hidden'
                                        }}>
                                            <div style={{
                                                width: `${Math.min(100, Math.round(parseFloat(project.completion_percentage) || 0))}%`,
                                                height: '100%',
                                                background: `linear-gradient(90deg, ${getStatusColor(project.status)}, ${getStatusColor(project.status)}dd)`,
                                                transition: 'width 0.3s ease'
                                            }}></div>
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', gap: '8px', marginBottom: '15px', flexWrap: 'wrap' }}>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                navigate(`/projects/${project.id}/boq`);
                                            }}
                                            style={{
                                                padding: '8px 14px',
                                                backgroundColor: 'white',
                                                color: brand.primary,
                                                border: `1px solid ${brand.primary}`,
                                                borderRadius: '6px',
                                                cursor: 'pointer',
                                                fontSize: '13px',
                                                fontWeight: '600'
                                            }}
                                        >
                                            📋 Materials Schedule
                                        </button>
                                    </div>

                                    {project.milestones && project.milestones.length > 0 && (
                                        <div style={{ marginBottom: '15px' }}>
                                            <h4 style={{ margin: '0 0 10px 0', color: text.primary, fontSize: '16px' }}>Milestones</h4>
                                            <div style={{ display: 'grid', gap: '8px' }}>
                                                {project.milestones.map(milestone => (
                                                    <div key={milestone.id} style={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        padding: '10px 14px',
                                                        backgroundColor: bg.subtle,
                                                        borderRadius: '6px',
                                                        fontSize: '14px'
                                                    }}>
                                                        <span>{milestone.name}</span>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                            <span style={{ fontWeight: '600' }}>{formatCurrency(milestone.amount)}</span>
                                                            <span style={{
                                                                padding: '3px 10px',
                                                                borderRadius: '12px',
                                                                backgroundColor: getStatusColor(milestone.status),
                                                                color: 'white',
                                                                fontSize: '11px',
                                                                fontWeight: 'bold'
                                                            }}>
                                                                {milestone.status.toUpperCase()}
                                                            </span>
                                                            {milestone.status === 'in_progress' && (
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleSubmitMilestoneForProject(project.id, milestone.id);
                                                                    }}
                                                                    style={{
                                                                        padding: '5px 12px',
                                                                        backgroundColor: status.successDark,
                                                                        color: 'white',
                                                                        border: 'none',
                                                                        borderRadius: '5px',
                                                                        cursor: 'pointer',
                                                                        fontSize: '11px',
                                                                        fontWeight: '600'
                                                                    }}
                                                                >
                                                                    Submit
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Recent Earnings */}
                <div style={{ 
                    backgroundColor: 'white', 
                    padding: '25px', 
                    borderRadius: '12px', 
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)' 
                }}>
                    <h2 style={{ color: text.primary, marginBottom: '20px', fontSize: '20px', fontWeight: '600' }}>
                        Recent Earnings
                    </h2>

                    {dashboardData.earnings.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '40px 20px', color: text.secondary }}>
                            <div style={{ fontSize: '48px', marginBottom: '10px' }}>💰</div>
                            <p>No earnings yet</p>
                        </div>
                    ) : (
                        <div>
                            {dashboardData.earnings.slice(0, 5).map((earning, index) => (
                                <div
                                    key={index}
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        padding: '15px 0',
                                        borderBottom: index < 4 ? '1px solid #f0f0f0' : 'none'
                                    }}
                                >
                                    <div>
                                        <div style={{ fontWeight: '600', marginBottom: '4px', fontSize: '14px' }}>
                                            {earning.project}
                                        </div>
                                        <div style={{ fontSize: '12px', color: text.secondary }}>
                                            {earning.month}
                                        </div>
                                    </div>
                                    <div style={{ fontWeight: '700', color: status.success, fontSize: '16px' }}>
                                        {formatCurrency(earning.amount)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    <button
                        onClick={handlePaymentHistory}
                        style={{
                            width: '100%',
                            marginTop: '20px',
                            padding: '12px',
                            backgroundColor: 'white',
                            color: brand.primary,
                            border: `2px solid ${brand.primary}`,
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '600',
                            transition: 'background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1), color 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}
                        onMouseOver={(e) => {
                            e.target.style.backgroundColor = brand.primary;
                            e.target.style.color = 'white';
                        }}
                        onMouseOut={(e) => {
                            e.target.style.backgroundColor = 'white';
                            e.target.style.color = brand.primary;
                        }}
                    >
                        View All Earnings
                    </button>
                </div>
            </div>

            {/* Withdrawal Dialog */}
            <WithdrawalDialog
                open={withdrawalDialogOpen}
                onClose={() => setWithdrawalDialogOpen(false)}
                availableBalance={availableBalance || 0}
                userId={user?.id}
                role="contractor"
                onSuccess={() => {
                    setWithdrawalDialogOpen(false);
                    // Refresh balance after withdrawal request
                    api.get('/payments/balance')
                        .then(res => setAvailableBalance(res.data?.data?.available ?? availableBalance))
                        .catch(() => {});
                }}
            />
        </div>
    );
};

export default ContractorDashboard;
